import { Dealer } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import { useAuth } from '@hooks';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  styled,
  SxProps,
  TextField,
} from '@mui/material';
import { Coordinates } from '@store/location/location.slice';
import { t } from '@translations';
import { calcDistance } from '@utils/location.utils';
import { lazy, Suspense } from 'react';
import { getPublicAddress } from '../dealers';

export type SearchDealerValue = string | Dealer | (string | Dealer)[] | null;

const LazyAutocomplete = lazy(() =>
  import('@mui/material/Autocomplete').then((module) => ({
    default: module.default as React.ComponentType<
      React.ComponentProps<typeof module.default<Dealer, boolean, boolean, boolean>>
    >,
  }))
);

type SearchDealerAutocompleteProps = {
  dealers: Dealer[];
  handleAutocompleteChange: (event: any, value: SearchDealerValue) => void;
  value?: SearchDealerValue;
  currentLocation?: Coordinates | null;
  isMultiple?: boolean;
  required?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  error?: boolean;
  freeSolo?: boolean;
  label?: string;
  placeholder?: string;
  busy?: boolean;
};

const StyledTextField = styled(TextField)`
  & .MuiInputBase-root {
    height: auto;
    min-height: 44px;
  }
  & .MuiInputBase-input {
    box-sizing: border-box;
  }
  & legend span {
    display: none;
  }
`;

export const SearchDealerAutocomplete = ({
  dealers,
  handleAutocompleteChange,
  value,
  currentLocation,
  isMultiple = false,
  required = false,
  disabled = false,
  sx,
  error,
  freeSolo,
  label,
  placeholder,
  busy,
}: SearchDealerAutocompleteProps) => {
  const { isAuthenticated } = useAuth();
  const getName = (dealer: Dealer) =>
    (isAuthenticated ? dealer?.name : getPublicAddress(dealer)?.name) || '';
  return (
    <Suspense fallback={null}>
      <LazyAutocomplete
        sx={sx}
        disablePortal
        disabled={disabled}
        id='dealer-search'
        options={dealers}
        getOptionLabel={(option) => {
          if (typeof option === 'string') {
            return option;
          }
          return getName(option) || option.customerNo || '';
        }}
        onChange={handleAutocompleteChange}
        onInputChange={(event, newInputValue, reason) => {
          if (reason === 'clear' && freeSolo) {
            handleAutocompleteChange(event, null);
          }
        }}
        value={value}
        multiple={isMultiple}
        isOptionEqualToValue={(option: Dealer, value: Dealer | Dealer[]) => {
          if (isAuthenticated) {
            return Array.isArray(value)
              ? value.some((v) => v.customerNo === option.customerNo)
              : option.customerNo === value.customerNo;
          } else {
            return Array.isArray(value)
              ? value.some((v) => getName(v) === getName(option))
              : getName(option) === getName(value);
          }
        }}
        freeSolo={freeSolo}
        slotProps={{
          popper: {
            sx: {
              zIndex: 100,
            },
          },
        }}
        filterOptions={(options, params) => {
          const searchText = (option: Dealer) => {
            if (isAuthenticated) {
              return (
                getName(option) +
                ' ' +
                (option.customerNo || '') +
                ' ' +
                option?.address?.map((a) => a.line1).join(' ')
              );
            } else {
              return getName(option) + ' ' + option?.address?.map((a) => a.line1).join(' ');
            }
          };

          const filtered = options.filter((option) => {
            return searchText(option).toLowerCase().includes(params.inputValue.toLowerCase());
          });

          const { inputValue } = params;
          let isExisting = false;

          if (isAuthenticated) {
            isExisting =
              options.some((option) => option.customerNo === inputValue) ||
              dealers.some((dealer) =>
                dealer?.name?.toLowerCase().includes(inputValue.toLowerCase())
              );
          } else {
            isExisting = dealers.some((dealer) =>
              dealer?.name?.toLowerCase().includes(inputValue.toLowerCase())
            );
          }

          if (!isExisting && freeSolo && isAuthenticated) {
            filtered.push({
              customerNo: inputValue,
            });
          }
          return filtered;
        }}
        ListboxProps={{ style: { maxHeight: '300px' } }}
        renderOption={(props, option: Dealer) => {
          const distance =
            currentLocation &&
            option.latitude &&
            option.longitude &&
            calcDistance({
              pointA: { latitude: option.latitude, longitude: option.longitude },
              pointB: currentLocation,
            });
          const address = getPublicAddress(option);
          return (
            <li {...props}>
              <Box display='flex' flexDirection='column'>
                <span>{getName(option)}</span>
                {address?.line1 ? (
                  <small>
                    {t(TextCodes.ProductRequest.Order.Address)}: {address?.line1}
                  </small>
                ) : null}
                {isAuthenticated && option?.customerNo ? (
                  <small>
                    {t(TextCodes.ProductRequest.Order.CustomerNo)}: {option?.customerNo}
                  </small>
                ) : null}
                {distance ? (
                  <small>
                    {t(TextCodes.ProductRequest.Order.EstimatedDistance)}: {distance.toFixed(1)} km
                  </small>
                ) : null}
              </Box>
            </li>
          );
        }}
        renderTags={(value: Dealer[], getTagProps) => {
          return (
            Array.isArray(value) &&
            value.map((option: Dealer, index: number) => (
              <Chip variant='outlined' label={getName(option)} {...getTagProps({ index })} />
            ))
          );
        }}
        renderInput={(params) => (
          <StyledTextField
            {...params}
            required={required}
            error={error}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <InputAdornment position='start'>
                    {busy ? <CircularProgress size={20} sx={{ mr: 1 }} /> : <SearchIcon />}
                  </InputAdornment>
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            type='text'
            variant='outlined'
            label={label || t(TextCodes.ProductRequest.Order.SelectDealer)}
            placeholder={placeholder || t(TextCodes.ProductRequest.Order.EnterDealerName)}
          />
        )}
      />
    </Suspense>
  );
};
